import React, { useMemo, useRef } from 'react'
import {
  useSequencerStore,
  selectCurrentProject,
  FX_IDS,
  PADS_PER_PROJECT,
  STEP_COUNTS,
  midiNoteName,
  type FxId,
  type StepCell,
  type TrackerRow,
} from '@/stores/sequencerStore'
import { KITS } from '@/hooks/useAudioEngine'
import { resolvePad, type ResolvedPad } from '@/lib/resolvePad'

// Mirror `StepGrid`'s long-press timing so tap / hold feel the same
// across layouts (2×8 / 8×8 / 4×4 / tracker).
const LONG_PRESS_MS = 320

// Polyend / Renoise show every possible step row, greying the ones
// outside the current pattern length so users can SEE where the loop
// ends. `STEP_COUNTS` includes [16, 32, 64, 128]; we render up to the
// highest supported value so the list doesn't resize when the user
// toggles between 32/64/128.
const MAX_TRACKER_ROWS = Math.max(...STEP_COUNTS)

interface TrackerLayoutProps {
  /**
   * Callback to open the PadEditor dialog focused on a specific
   * pad index — wired by the sequencer page so the tracker can
   * offer a "+ Add channel" affordance that takes users straight
   * to configuring an empty slot.
   */
  onEditPad: (padIndex: number | null) => void
}

interface TrackerColumn {
  row: TrackerRow
  key: string
  /** Displayed as the one-or-two-char header. */
  shortLabel: string
  /** Full name shown in the sub-header. */
  label: string
  /** UI accent — 'accent' for synth, 'second' for MIDI, 'sample' for
   *  sample pads, 'granular' for granular pads. */
  accent: 'accent' | 'second' | 'sample' | 'granular'
  /** Pad kind for the header chip (synth/sample/midi). */
  kind?: ResolvedPad['kind']
  /** Base MIDI note of the pad. Cells with `cell.pitch ≠ 0` render
   *  the note name as `midiNoteName(basePadNote + pitch)` so users
   *  can read the Tracker pitch at a glance. */
  basePadNote?: number
}

/** Per-FX accent colours. Matches the FX rail so users can map
 *  tracker cell dots → FX bank buttons by colour. Kept in the
 *  same declaration order as `FX_IDS` (filter / delay / reverb /
 *  bitcrush / tapeStop / stutter). Filter + tapeStop are serial-
 *  only effects that aren't surfaced as per-step sends, so cells
 *  never render dots for them — the map still has entries to keep
 *  the type exhaustive. */
const FX_DOT_COLORS: Record<FxId, string> = {
  filter: 'bg-teal-400',
  delay: 'bg-sky-400',
  reverb: 'bg-emerald-400',
  bitcrush: 'bg-rose-400',
  tapeStop: 'bg-amber-400',
  stutter: 'bg-violet-400',
}

/**
 * Polyend-inspired tracker view. Rows = steps, columns = pads. Each
 * cell is a compact indicator (on/off + optional rich-data dot).
 * Click a cell to focus it in the inspector; that also toggles
 * on/off on first click (so single-click still edits quickly).
 *
 * This component now renders ONLY the grid (+ the "+ Add channel"
 * header). The `<TrackerInspector>` used to live side-by-side here;
 * Phase 5 of the sequencer overhaul moves it into the pad-grid
 * column of the 3-col editor layout so tracker mode stops replacing
 * the whole editor. See `PadGrid.tsx` for where the inspector
 * currently renders.
 *
 * Only pads that the current kit fills are shown as columns — empty
 * pads are dropped so the grid isn't padded with dead cells.
 */
export const TrackerLayout: React.FC<TrackerLayoutProps> = ({ onEditPad }) => {
  const project = useSequencerStore(selectCurrentProject)
  const isPlaying = useSequencerStore(s => s.isPlaying)
  const currentStep = useSequencerStore(s => s.currentStep)
  const trackerFocus = useSequencerStore(s => s.trackerFocus)
  const toggleStep = useSequencerStore(s => s.toggleStep)
  const setTrackerFocus = useSequencerStore(s => s.setTrackerFocus)

  const kit = KITS[project.kitId]

  // Resolve every pad (0..15) the same way the main pad grid
  // does — this picks up synth / sample / external-MIDI
  // overrides that land in padDefs beyond the kit's default 8
  // voices.
  const resolvedPads = useMemo<Array<ResolvedPad | null>>(() => {
    const kitSlots: Array<(typeof kit.voices)[number] | undefined> = new Array(
      PADS_PER_PROJECT,
    ).fill(undefined)
    kit.voices.forEach((v, i) => {
      kitSlots[i] = v
    })
    return Array.from({ length: PADS_PER_PROJECT }, (_, i) =>
      resolvePad(project, kitSlots, i),
    )
  }, [kit, project])

  // Column layout: every occupied slot (synth / sample / midi)
  // followed by overflow MIDI pads. Each column carries the
  // pad's kind so the header can badge it appropriately.
  const columns = useMemo<TrackerColumn[]>(() => {
    const result: TrackerColumn[] = []
    resolvedPads.forEach((pad, i) => {
      if (!pad) return
      result.push({
        row: { kind: 'kit', padIndex: i },
        key: `kit-${i}`,
        shortLabel: String(i + 1),
        label: pad.label,
        accent:
          pad.kind === 'midi'
            ? 'second'
            : pad.kind === 'sample'
              ? 'sample'
              : pad.kind === 'granular'
                ? 'granular'
                : 'accent',
        kind: pad.kind,
        basePadNote: pad.midiNote,
      })
    })
    project.midiPads.forEach((mp, i) => {
      result.push({
        row: { kind: 'midi', padId: mp.id },
        key: `midi-${mp.id}`,
        shortLabel: `M${i + 1}`,
        label: mp.label,
        accent: 'second',
        kind: 'midi',
        basePadNote: mp.note,
      })
    })
    return result
  }, [resolvedPads, project.midiPads])

  // First empty kit-pad slot, used by the "+ Add channel"
  // affordance. Returns null if all 16 slots are occupied —
  // the button is then disabled with a hint.
  const firstEmptySlot = useMemo<number | null>(() => {
    const idx = resolvedPads.findIndex(p => p === null)
    return idx === -1 ? null : idx
  }, [resolvedPads])

  // Tap toggles; long-press + Shift-click + Ctrl/Cmd-click + right-
  // click all open the cell in the inspector. Matches the step-grid
  // contract so users get the same muscle memory across layouts.
  const handleToggle = (col: TrackerColumn, stepIndex: number) => {
    toggleStep(col.row, stepIndex)
    // Toggling a cell also focuses it so the bottom inspector shows
    // the cell the user just touched. Without this, turning a step
    // on leaves the inspector pointed at an unrelated cell.
    setTrackerFocus({ row: col.row, stepIndex })
  }
  const handleOpenInspector = (col: TrackerColumn, stepIndex: number) => {
    setTrackerFocus({ row: col.row, stepIndex })
  }

  return (
    <div className="flex flex-col gap-2 min-w-0">
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-content-tertiary">
          {columns.length} channel{columns.length === 1 ? '' : 's'}
        </span>
        <button
          type="button"
          onClick={() => {
            if (firstEmptySlot !== null) onEditPad(firstEmptySlot)
            else onEditPad(null)
          }}
          disabled={firstEmptySlot === null}
          title={
            firstEmptySlot !== null
              ? `Add a channel at pad ${firstEmptySlot + 1}`
              : 'All 16 slots occupied — use overflow MIDI pads for more'
          }
          className="text-[11px] font-mono text-accent hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          + Add channel
        </button>
      </div>
      <TrackerGrid
        project={project}
        columns={columns}
        isPlaying={isPlaying}
        currentStep={currentStep}
        focus={trackerFocus}
        onToggle={handleToggle}
        onOpenInspector={handleOpenInspector}
      />
    </div>
  )
}

function rowEquals(a: TrackerRow | undefined, b: TrackerRow | undefined) {
  if (!a || !b) return false
  if (a.kind === 'kit' && b.kind === 'kit') return a.padIndex === b.padIndex
  if (a.kind === 'midi' && b.kind === 'midi') return a.padId === b.padId
  return false
}

// ── Inner grid ──────────────────────────────────────────────

interface TrackerGridProps {
  project: ReturnType<typeof selectCurrentProject>
  columns: TrackerColumn[]
  isPlaying: boolean
  currentStep: number
  focus: ReturnType<typeof useSequencerStore.getState>['trackerFocus']
  onToggle: (col: TrackerColumn, stepIndex: number) => void
  onOpenInspector: (col: TrackerColumn, stepIndex: number) => void
}

function cellFor(
  project: ReturnType<typeof selectCurrentProject>,
  row: TrackerRow,
  stepIdx: number,
): StepCell | undefined {
  if (row.kind === 'kit') {
    return (project.steps[row.padIndex] ?? [])[stepIdx]
  }
  return (project.midiPadSteps[row.padId] ?? [])[stepIdx]
}

const TrackerGrid: React.FC<TrackerGridProps> = ({
  project,
  columns,
  isPlaying,
  currentStep,
  focus,
  onToggle,
  onOpenInspector,
}) => {
  const stepCount = project.stepCount
  // Per-pad mute map for the active bank. Column headers render a
  // small toggle button; muted columns fade their cells so the user
  // knows the fire path drops those hits.
  const activeBankId = useSequencerStore(s => {
    const c = s.containers[s.currentContainerId]
    return c?.activeBankId ?? null
  })
  const padMutedMap = useSequencerStore(s =>
    activeBankId ? (s.padMuted[activeBankId] ?? {}) : {},
  )
  const setPadMuted = useSequencerStore(s => s.setPadMuted)
  // Row-range loop — when set, scheduler wraps `loopOut+1 → loopIn`.
  // Tracker gutter renders two markers + dims out-of-range rows so
  // the looping window is visually obvious. Clicking the gutter
  // cell promotes it to the new loop endpoint.
  const loopInStep =
    typeof project.loopInStep === 'number' ? project.loopInStep : null
  const loopOutStep =
    typeof project.loopOutStep === 'number' ? project.loopOutStep : null
  const hasLoopRange =
    loopInStep !== null &&
    loopOutStep !== null &&
    loopOutStep >= loopInStep &&
    loopOutStep < stepCount
  const setPatternLoopRange = useSequencerStore(s => s.setPatternLoopRange)

  const handleLoopGutterClick = (stepIdx: number, e: React.MouseEvent) => {
    // Shift-click a step row number → set the loop-out endpoint.
    // Plain click → set the loop-in endpoint (and drop the out if it
    // would now be below in). Double-click anywhere with a range set
    // clears the range entirely. Gives single-click-to-set with a
    // natural "set range by clicking start then Shift-clicking end"
    // flow that matches how DAWs typically expose this.
    if (e.detail === 2) {
      setPatternLoopRange(null, null)
      return
    }
    if (e.shiftKey && loopInStep !== null) {
      setPatternLoopRange(loopInStep, stepIdx)
      return
    }
    // Start a fresh range at this step. Default the end to the same
    // step so the user sees a 1-row range and can Shift-click to
    // extend it.
    setPatternLoopRange(stepIdx, loopOutStep ?? stepIdx)
  }

  return (
    <div className="rounded-xl bg-surface-sunken border border-brd-subtle p-3 flex flex-col max-h-[calc(100dvh-14rem)]">
      <div className="flex items-center justify-between mb-2 shrink-0">
        <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-content-tertiary">
          Tracker · {stepCount} / {MAX_TRACKER_ROWS} steps × {columns.length}{' '}
          pads · tap to toggle · hold / shift-click to edit
        </span>
        <span className="text-[10px] font-mono text-content-tertiary">
          {project.kitId}
        </span>
      </div>
      <div className="overflow-auto flex-1 min-h-0">
        <table className="border-collapse font-mono text-[10px] select-none">
          <thead>
            <tr>
              <th className="sticky left-0 z-10 bg-surface-sunken text-content-tertiary font-normal px-2 py-1 text-right w-12">
                Step
              </th>
              {columns.map(col => (
                <th
                  key={col.key}
                  title={col.label}
                  className={`font-normal uppercase tracking-wider px-1 py-1 w-9 truncate max-w-9 ${
                    col.accent === 'second'
                      ? 'text-second'
                      : 'text-content-tertiary'
                  }`}
                >
                  {col.shortLabel}
                </th>
              ))}
            </tr>
            <tr>
              <th className="sticky left-0 z-10 bg-surface-sunken" />
              {columns.map(col => (
                <th
                  key={`name-${col.key}`}
                  className="text-content-secondary font-normal px-1 pb-1 text-[9px] truncate max-w-9"
                >
                  {col.label.split(' ')[0]}
                </th>
              ))}
            </tr>
            {/* Mute row — per-pad toggle on kit pads. MIDI-overflow
                pads aren't indexed by padIndex so they share the
                bank-level mute only; their header cell stays empty. */}
            <tr>
              <th className="sticky left-0 z-10 bg-surface-sunken" />
              {columns.map(col => {
                const padIndex =
                  col.row.kind === 'kit' ? col.row.padIndex : null
                const muted =
                  padIndex !== null && padMutedMap[padIndex] === true
                return (
                  <th
                    key={`mute-${col.key}`}
                    className="font-normal px-0.5 pb-1.5 text-center"
                  >
                    {padIndex !== null ? (
                      <button
                        type="button"
                        onClick={() => {
                          if (activeBankId) setPadMuted(activeBankId, padIndex)
                        }}
                        aria-pressed={muted}
                        title={
                          muted
                            ? `Pad ${padIndex + 1} muted — tap to unmute`
                            : `Mute pad ${padIndex + 1}`
                        }
                        className={`w-5 h-3.5 rounded-sm text-[8px] leading-none font-mono tracking-[0.08em] transition-colors ${
                          muted
                            ? 'bg-destructive/20 text-destructive border border-destructive/60'
                            : 'bg-surface-sunken text-content-tertiary/70 border border-brd-subtle hover:border-[color:var(--accent-halo)] hover:text-accent'
                        }`}
                      >
                        {muted ? 'M' : 'm'}
                      </button>
                    ) : null}
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: MAX_TRACKER_ROWS }).map((_, stepIdx) => {
              const isDownbeat = stepIdx % 4 === 0
              const isPlayhead = isPlaying && currentStep === stepIdx
              const inactive = stepIdx >= stepCount
              const outOfLoop =
                hasLoopRange &&
                !inactive &&
                (stepIdx < loopInStep! || stepIdx > loopOutStep!)
              const isLoopIn = hasLoopRange && stepIdx === loopInStep
              const isLoopOut = hasLoopRange && stepIdx === loopOutStep
              return (
                <tr
                  key={stepIdx}
                  className={[
                    isPlayhead ? 'bg-[color:var(--second-soft)]' : '',
                    inactive ? 'opacity-40' : '',
                    outOfLoop ? 'opacity-40' : '',
                  ].join(' ')}
                >
                  <th
                    scope="row"
                    onClick={
                      inactive
                        ? undefined
                        : e => handleLoopGutterClick(stepIdx, e)
                    }
                    title={
                      inactive
                        ? undefined
                        : hasLoopRange
                          ? `Step ${stepIdx + 1} — click to move loop in, shift-click for loop out, double-click to clear (${loopOutStep! - loopInStep! + 1}-row loop active)`
                          : `Step ${stepIdx + 1} — click to start a row-range loop here`
                    }
                    className={`sticky left-0 z-10 font-normal text-right px-2 py-0.5 w-12 relative ${
                      inactive
                        ? 'bg-surface-sunken/60 text-content-tertiary/40'
                        : isDownbeat
                          ? 'bg-surface-sunken text-content-secondary cursor-pointer'
                          : 'bg-surface-sunken text-content-tertiary/70 cursor-pointer'
                    } ${isLoopIn ? 'text-[color:var(--accent-ink)] bg-accent/70' : ''} ${isLoopOut ? 'text-[color:var(--destructive-ink,white)] bg-destructive/70' : ''}`}
                  >
                    {/* Subtle left-edge bar runs through the in-range
                        window so the loop is visible even when the
                        gutter is scrolled out of the viewport's top
                        or bottom. */}
                    {hasLoopRange &&
                      !inactive &&
                      stepIdx >= loopInStep! &&
                      stepIdx <= loopOutStep! &&
                      !isLoopIn &&
                      !isLoopOut && (
                        <span
                          aria-hidden
                          className="absolute left-0 top-0 bottom-0 w-0.5 bg-accent/70"
                        />
                      )}
                    {String(stepIdx + 1).padStart(2, '0')}
                  </th>
                  {columns.map(col => {
                    const cell = cellFor(project, col.row, stepIdx)
                    const focused =
                      !inactive &&
                      rowEquals(focus?.row, col.row) &&
                      focus?.stepIndex === stepIdx
                    const colPadIndex =
                      col.row.kind === 'kit' ? col.row.padIndex : null
                    const muted =
                      colPadIndex !== null && padMutedMap[colPadIndex] === true
                    return (
                      <TrackerCell
                        key={col.key}
                        cell={cell}
                        focused={focused}
                        isDownbeat={isDownbeat}
                        accent={col.accent}
                        basePadNote={col.basePadNote}
                        inactive={inactive}
                        muted={muted}
                        onToggle={() => {
                          if (inactive) return
                          onToggle(col, stepIdx)
                        }}
                        onOpenInspector={() => {
                          if (inactive) return
                          onOpenInspector(col, stepIdx)
                        }}
                      />
                    )
                  })}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

interface TrackerCellProps {
  cell: StepCell | undefined
  focused: boolean
  isDownbeat: boolean
  accent: 'accent' | 'second' | 'sample' | 'granular'
  /** Base MIDI note of the owning pad. Used to render cell.pitch
   *  as an absolute note name (C-3 etc). Undefined falls back to
   *  the plain bullet render. */
  basePadNote?: number
  /**
   * True when this cell is beyond the pattern's current step length
   * (Polyend/Renoise style — the row is rendered but disabled so the
   * user can see where the loop ends and the pattern would resume).
   */
  inactive?: boolean
  /** Column is pad-muted. Fades the cell so the user knows the fire
   *  path drops the hit even though the step is still `on`. Editing
   *  still works at full fidelity. */
  muted?: boolean
  /** Tap gesture: flip on/off. Matches the step grid. */
  onToggle: () => void
  /** Open the cell in the bottom inspector. Fires on long-press,
   *  Shift-click, Ctrl/Cmd-click, and right-click. */
  onOpenInspector: () => void
}

const TrackerCell: React.FC<TrackerCellProps> = ({
  cell,
  focused,
  isDownbeat,
  accent,
  basePadNote,
  inactive = false,
  muted = false,
  onToggle,
  onOpenInspector,
}) => {
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  // True once long-press / modifier-click already opened the inspector
  // for this gesture so the trailing `click` event doesn't double-dip
  // and also toggle the cell.
  const inspectorFired = useRef(false)

  const on = cell?.on ?? false
  const onClasses =
    accent === 'second'
      ? 'bg-second/80 border-second text-[color:var(--second-ink,white)] font-bold'
      : accent === 'sample'
        ? 'bg-accent/60 border-accent text-[color:var(--accent-ink)] font-bold'
        : accent === 'granular'
          ? 'bg-accent/90 border-accent ring-1 ring-inset ring-[color:var(--accent-halo)] text-[color:var(--accent-ink)] font-bold'
          : 'bg-accent/80 border-accent text-[color:var(--accent-ink)] font-bold'

  // Progressive-disclosure indicators. Each is its own overlay so
  // cells reveal exactly what overrides they carry — no generic
  // "has extras" dot.
  const hasPitch = on && cell?.pitch !== undefined && cell.pitch !== 0
  const noteLabel =
    hasPitch && basePadNote !== undefined
      ? midiNoteName(basePadNote + (cell?.pitch ?? 0))
      : null
  const velocity = on && cell?.velocity !== undefined ? cell.velocity : null
  const activeFx =
    on && cell?.fx
      ? FX_IDS.filter(fx => {
          const v = cell.fx?.[fx]
          return typeof v === 'number' && v > 0.02
        })
      : []
  const rollSubs = on && cell?.roll ? Math.min(8, cell.roll.subdivisions) : 0
  const reversed = on && cell?.reverse === true

  const cancelLongPress = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current)
      longPressTimer.current = null
    }
  }

  const handlePointerDown = (e: React.PointerEvent<HTMLTableCellElement>) => {
    if (inactive) return
    inspectorFired.current = false
    // Shift / Ctrl / Cmd / right-button: open the inspector right
    // away without waiting for the hold timer. Shift is the desktop
    // keyboard shortcut the user asked for.
    if (e.button === 2 || e.ctrlKey || e.metaKey || e.shiftKey) {
      e.preventDefault()
      inspectorFired.current = true
      onOpenInspector()
      return
    }
    longPressTimer.current = setTimeout(() => {
      inspectorFired.current = true
      onOpenInspector()
    }, LONG_PRESS_MS)
  }

  const handleClick = () => {
    if (inactive) return
    if (inspectorFired.current) {
      // Long-press or Shift-click already fired — don't toggle.
      inspectorFired.current = false
      return
    }
    onToggle()
  }

  return (
    <td
      onClick={handleClick}
      onPointerDown={handlePointerDown}
      onPointerUp={cancelLongPress}
      onPointerLeave={cancelLongPress}
      onPointerCancel={cancelLongPress}
      onContextMenu={e => {
        if (inactive) return
        e.preventDefault()
        inspectorFired.current = true
        onOpenInspector()
      }}
      role={inactive ? 'presentation' : 'button'}
      tabIndex={inactive ? -1 : 0}
      aria-disabled={inactive || undefined}
      aria-pressed={on}
      className={[
        'text-center select-none p-0 relative',
        inactive ? 'cursor-default' : 'cursor-pointer',
        isDownbeat && !inactive ? 'bg-surface-raised/40' : '',
        // Focus halo uses the theme's tertiary text token so it reads
        // as "marked for editing" without competing with the accent
        // (`on`) fill or the second (playhead) highlight.
        focused
          ? 'bg-content-tertiary/20 ring-1 ring-content-tertiary ring-offset-1 ring-offset-surface-sunken'
          : '',
        muted ? 'opacity-40' : '',
      ].join(' ')}
      style={{ width: '36px', height: '22px' }}
    >
      <span
        className={[
          'block w-full h-full leading-[22px] rounded-sm border',
          on
            ? onClasses
            : inactive
              ? 'border-transparent text-content-tertiary/30'
              : focused
                ? 'border-transparent text-content-tertiary'
                : 'border-transparent text-content-tertiary/60 hover:bg-surface-raised/60',
        ].join(' ')}
      >
        {/* Note name takes over the glyph when pitch ≠ 0. Otherwise
            the usual ● / · bullet. Note font slightly smaller so
            "G#3" fits in the 36 px cell. */}
        {on && noteLabel ? (
          <span className="text-[9px] font-bold tracking-tight">
            {noteLabel}
          </span>
        ) : on ? (
          '●'
        ) : (
          '·'
        )}
      </span>
      {/* Velocity meter — 2 px bar on the left edge, height ∝ vel.
          Rendered inside the td so it respects muted opacity. */}
      {velocity !== null && !inactive && (
        <span
          aria-hidden
          className="absolute left-0.5 bottom-0.5 w-[2px] rounded-sm bg-[color:var(--accent-ink)]/60 pointer-events-none"
          style={{ height: `${Math.max(2, Math.round(velocity * 18))}px` }}
        />
      )}
      {/* FX send dots stacked on the right edge, one per active FX.
          Colours match the FX rail buttons so the user maps
          Tracker → rail without a legend. */}
      {activeFx.length > 0 && !inactive && (
        <span
          aria-hidden
          className="absolute right-0.5 top-0.5 flex flex-col gap-[1px] pointer-events-none"
        >
          {activeFx.map(fx => (
            <span
              key={fx}
              className={`block w-1 h-1 rounded-full ${FX_DOT_COLORS[fx]}`}
            />
          ))}
        </span>
      )}
      {/* Roll subdivision indicator — dash chain along the bottom.
          Capped at 8 dashes so a 16-subdivision roll doesn't over-
          fill the cell; users read the count as "several" past 8
          and read the exact count in the inspector. */}
      {rollSubs > 0 && !inactive && (
        <span
          aria-hidden
          className="absolute bottom-0 left-1 right-1 flex gap-[1px] justify-center pointer-events-none"
        >
          {Array.from({ length: rollSubs }).map((_, i) => (
            <span
              key={i}
              className="block h-[2px] flex-1 max-w-[4px] rounded-sm bg-[color:var(--second)]/85"
            />
          ))}
        </span>
      )}
      {/* Reverse glyph — tucked into the top-left where a focus halo
          doesn't compete. Small + muted so it's noticeable only when
          the user is scanning for reversed hits. */}
      {reversed && !inactive && (
        <span
          aria-hidden
          className="absolute top-0 left-0.5 text-[8px] text-[color:var(--accent-ink)]/80 leading-none pointer-events-none"
        >
          ⇆
        </span>
      )}
    </td>
  )
}
